import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  AdvertiserDto,
  AdvertiserFileDto,
  CustomReportDto,
  OfflineConversionsUploadConfigOutputDto,
  OfflineConversionsUploadUrlOutputDto,
} from '@kortxio/hub-api';
import axios from 'axios';
import { setError } from 'features/error/slice';
import api from 'libs/api';
import { parseApiPayload, toApiError } from 'libs/api/helper';
import { AsyncThunkConfig } from 'store/types';

export const getSelectedAdvertiserById = createAsyncThunk<
  AdvertiserDto,
  { clientId: number; id: number }
>(
  'advertiserById/getSelectedAdvertiserById',
  async ({ clientId, id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.advertiser.getAdvertiserByClientIdAndId(
        clientId,
        id
      );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }
      return rejectWithValue(apiError);
    }
  }
);

export const getCustomReportsForAdvertiser = createAsyncThunk<
  CustomReportDto[],
  {
    advertiserId: number;
  }
>(
  'advertisersById/getCustomReportsForAdvertiser',
  async ({ advertiserId }, { dispatch, rejectWithValue }) => {
    try {
      const response =
        await api.customReporting.getCustomReportsForAdvertiser(advertiserId);

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);
      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }
      return rejectWithValue(apiError);
    }
  }
);

export const createCustomReportForAdvertiser = createAsyncThunk<
  CustomReportDto,
  {
    advertiserId: number;
    customReport: CustomReportDto;
  }
>(
  'advertisersById/createCustomReportForAdvertiser',
  async ({ advertiserId, customReport }, { dispatch, rejectWithValue }) => {
    try {
      const response =
        await api.customReporting.createCustomReportForAdvertiser(
          advertiserId,
          customReport
        );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);
      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }
      return rejectWithValue(apiError);
    }
  }
);

export const deleteCustomReportForAdvertiser = createAsyncThunk<
  void,
  {
    advertiserId: number;
    customReportId: number;
  }
>(
  'advertisersById/deleteCustomReportForAdvertiser',
  async ({ advertiserId, customReportId }, { dispatch, rejectWithValue }) => {
    try {
      const response =
        await api.customReporting.deleteCustomReportForAdvertiser(
          advertiserId,
          customReportId
        );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);
      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }
      return rejectWithValue(apiError);
    }
  }
);

export const getAllFilesByAdvertiserId = createAsyncThunk<
  AdvertiserFileDto[],
  { clientId: number; advertiserId: number },
  AsyncThunkConfig
>(
  'advertiser/getAdvertiserFiles',
  async ({ clientId, advertiserId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.file.getAdvertiserFiles(
        clientId,
        advertiserId
      );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (apiError.isApi404Error) {
        return [];
      }

      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const postUploadClientAdvertiserFiles = createAsyncThunk<
  void,
  {
    clientId: number;
    advertiserId: number;
    files: File[];
  },
  AsyncThunkConfig
>(
  'advertiser/postUploadClientAdvertiserFiles',
  async ({ clientId, advertiserId, files }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.file.postUploadClientAdvertiserFiles(
        advertiserId,
        files
      );

      await dispatch(getAllFilesByAdvertiserId({ clientId, advertiserId }));

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const getOfflineConversionsUploadConfig = createAsyncThunk<
  OfflineConversionsUploadConfigOutputDto,
  { clientId: number; advertiserId: number }
>(
  'advertiserById/getOfflineConversionsUploadConfig',
  async ({ clientId, advertiserId }, { dispatch, rejectWithValue }) => {
    try {
      const response =
        await api.offlineConversionsUpload.getOfflineConversionsUploadConfigByClientIdAndAdvertiserId(
          clientId,
          advertiserId
        );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }
      return rejectWithValue(apiError);
    }
  }
);

export const postOfflineConversionsUploadUrl = createAsyncThunk<
  void,
  { clientId: number; advertiserId: number; file: File }
>(
  'advertiserById/postOfflineConversionsUploadUrl',
  async ({ clientId, advertiserId, file }, { dispatch, rejectWithValue }) => {
    try {
      const response =
        await api.offlineConversionsUpload.postOfflineConversionsUploadUrl(
          clientId,
          advertiserId,
          {
            contentType: file.type,
            contentLength: file.size,
          }
        );

      const data: OfflineConversionsUploadUrlOutputDto =
        parseApiPayload(response);

      if (!data.url) {
        throw new Error(
          'postOfflineConversionsUploadUrl missing Presigned URL'
        );
      }

      await axios.put(data.url, file, {
        headers: {
          'Content-Type': file.type,
        },
      });

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }
      return rejectWithValue(apiError);
    }
  }
);
